<template>
    <div 
        class="page-details"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <details-header
            ref="showInfo"
            :item="item"
            :is-playable="isPlayable"
            design="channels"
            :legacy-play="true"
            :channels="channels"
            :current-channel="currentChannel"
            @unset-focused="focusFirstRow"
            @play="play"
            @show-trailer="showTrailer"
        />
        <div>
            <Lines-detail-program 
                ref="linesDetailProgram" 
                @ready="readyLines"
                @list-item-selected="itemsSelected"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import LinesDetailProgram from "@/components/widgets/LinesDetailProgram/LinesDetailProgram.vue";
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import {AppConstants} from "@/common";
    import type {DetailsHeaderRef} from "@/components/widgets/DetailsHeader/types";
    import type { ProgramCardData} from "@/components/Cards/types";
    import { linkItems} from "@/components/Header/types/LinkItemsCollection";
    import { useApplicationStore } from "@/stores/Application";
    import { Logger, TimeUtils } from '@/utils';
    import type { LinesDetailProgramRef } from "@/components/widgets/LinesDetailProgram/types";
    import { computed, ref, type Ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
    import { useRouter } from 'vue-router';
    import { storeToRefs } from 'pinia';
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useDetailProgramPageStore } from "@/stores/DetailProgramPage";
    import { useFocus } from "@/composables/useFocus";
    import { usePlayerStore } from "@/stores/Player";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useAccountStore } from "@/stores/Account";
    import { Strings } from "@/locales/types/enums/strings";
    import { ScreenName } from "@/common/types/screen-names";
    import { Param as AnalyticsParam } from "@/plugins/analytics/interfaces/analytics.service.interface";


    const DetailPageStoreModule = useDetailPageStore();
    const {
        setViewedDetailItems
    } = DetailPageStoreModule;

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        showPreviewSignInModal, 
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingProgram
    } = PlayerStoreModule;
    const {
        playProgram,
    } = storeToRefs(PlayerStoreModule);
    const ParentControlStoreModule = useParentControlStore();
    const AccountStoreModule = useAccountStore()
    const {
        getForgotModalVisible: isForgotModalVisible
    } = storeToRefs(AccountStoreModule);
    const {
        getIsModalParentVisible: isTypeModeEnabledPin
    } = storeToRefs(ParentControlStoreModule);

    const { setIsHaveFocusedElement, focusElementLine } = useFocus();

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-detail-program'],
    });

    const { $fire, $eventBus, $navigator, $analytics } = useNuxtApp();
    const { t } = useI18n();

    const DetailPage = useDetailPageStore();
    const {
        getPlayBtnUnavailable,
        getCurrentItemDetail: selectedProgram
    } = storeToRefs(DetailPage);

    const DetailProgramPage = useDetailProgramPageStore();
    const {
        getRelatedChannels: channels,
        getCurrentChannel: currentChannel,
        getTimerIdPrograms
    } = storeToRefs(DetailProgramPage);
    
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const router = useRouter();

    const traceDetailsScreen = $fire.trace($fire.performance, 'DETAILS_SCREEN PROGRAM');
    try {
        traceDetailsScreen.start();
        Logger.captureLog('DETAILS_SCREEN PROGRAM start');
    } catch (e){
        Logger.captureLog(e);
    }

    
    const item = computed((): PlayItem | null => selectedProgram.value);
    const showInfo: Ref<DetailsHeaderRef | null> = ref(null);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const isReadyLines: Ref<boolean> = ref(false);
    const selectedFutureProgram: Ref<PlayItem | null> = ref(null);
    const linesDetailProgram: Ref<LinesDetailProgramRef | null>= ref(null);
    const isLoadedComponents = computed((): boolean => linesDetailProgram.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);
    const getProgramsTitle = computed((): string => currentChannel.value?.name ?? "");

    const isPlayable = computed((): boolean => {
        if (item.value && !playBtnUnavailable.value) {
            return PlayItem.isLive(item.value) || PlayItem.isVCloud(item.value, currentChannel.value);
        }
        return false;
    });
    const currentProgram = computed((): PlayItem | null => playProgram.value || selectedProgram.value);
    const isFuture = computed((): boolean => {
        const startDate = PlayItem.getStartTime(selectedFutureProgram.value || currentProgram.value);
        const timeDiff = TimeUtils.getTime() - startDate!.getTime();
        if (timeDiff < 0) {
            return true;
        }
        return false;
    });
    const playBtnUnavailable = computed((): boolean => getPlayBtnUnavailable.value && getProgramsTitle.value.length > 0);
    const allowRegistrationKeyListeners = computed((): boolean => !isTypeModeEnabledPin.value && !isForgotModalVisible.value);

    const focusToDetailHeader = (): void => {
        if (showInfo.value) {
            showInfo.value.focus();
        }
    };
    const readyLines = (): void => {
        isReadyLines.value = true;
    };
    const showTrailer = (trailerVideoId: string): void => {
        if (trailerVideoId) {
            void router.push(`${linkItems.trailer.routerPath}?youtubeVideoId=${trailerVideoId}`);
        }
    };
    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyListeners.value = false;
        }
    };
    const showSignInModal = (): void => {
        setTimeout(() => {
            showPreviewSignInModal(true);
        }, 150);
        
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const itemsSelected = (val: IEventSelected): void => {
        switch (val.lineId) {
            case AppConstants.shelfKeys.programsCards:
                onProgramSelected(val.item as ProgramCardData);
                break;

        }
    };
    const onProgramSelected = (programItem: ProgramCardData): void => {
        selectedFutureProgram.value = programItem.getProgram();

        if (!selectedFutureProgram.value) {
            return;
        }

        if (isGuest.value) {
            showSignInModal();
            return
        }
        const startDate = PlayItem.getStartTime(selectedFutureProgram.value);
        if (startDate) {
            const timeDiff = TimeUtils.getTime() - startDate.getTime();
            if (timeDiff < 0) { //in future
                play();
                return;
            }
        }
        startPlayingProgram(selectedFutureProgram.value);
        play();
    };

    const focusElement = (): void => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusToDetailHeader();
        }
    };

    const focusFirstRow = (): void => {
        if (linesDetailProgram.value) {
            linesDetailProgram.value.focusFirstRow();
        }
    };
    const play = (): void => {
        if (isFuture.value) {
            goFutureProgramPage();
            return;
        }
        
        if (isGuest.value) {
            showSignInModal();
        } else {
            void router.push(linkItems.player.routerPath);
        }
    };
    const goFutureProgramPage = (): void => {
        try {
            if (!selectedFutureProgram.value) {
                throw new Error("selectedFutureProgram is null in goFutureProgramPage");
            }
            setViewedDetailItems(selectedFutureProgram.value);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${selectedFutureProgram.value.id}`);
        } catch (error) {
            Logger.captureLog(error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const cancelAutoUpdate = (): void => {
        getTimerIdPrograms.value?.stop();
    };
    const initFocus = (): void => {
        setIsHaveFocusedElement();
        focusElement();
    };

    const handlerReady = (): void => {
       void nextTick(() => {
            initFocus();
            if (traceDetailsScreen) {
                try {
                    traceDetailsScreen.stop();
                    Logger.captureLog(`DETAILS_SCREEN PROGRAM stop ${traceDetailsScreen.durationUs / 1000000} sec`);
                } catch (e){
                    Logger.captureLog(e);
                }
            }
        });
    };

    watch(
        isReadyAll,
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );
    watch(
        allowRegistrationKeyListeners,
        (value: boolean) => {
            if (value) {
                initFocus();
            } else {
                unregisterKeyListeners();
            }
        }
    );
   
    onMounted(() => {
       void nextTick(() => {
            $eventBus.on("key-up-rows", focusToDetailHeader);
            if (selectedProgram.value) {
                const programId = selectedProgram.value?.recentAirTime?.id ?? selectedProgram.value?.id;
                
                $analytics.createCustomScreenEvent({
                    [AnalyticsParam.FA_SCREEN_NAME]: ScreenName.PROGRAM_DETAILS,
                    [AnalyticsParam.FA_SCREEN_CLASS]: ScreenName.PROGRAM_DETAILS,
                    [AnalyticsParam.ITEM_ID]: programId ? String(programId) : null,
                    [AnalyticsParam.CONTENT_TYPE]: selectedProgram.value?.contentType,
                    [AnalyticsParam.CHANNEL_ID]: selectedProgram.value?.recentAirTime?.channelId ?? currentChannel.value?.id,
                    [AnalyticsParam.CHANNEL_NAME]: currentChannel.value?.name,
                    [AnalyticsParam.PROGRAM_NAME]: selectedProgram.value?.name,
                })
            }
        });
    });

    onBeforeUnmount(() => {
        $eventBus.off("key-up-rows", focusToDetailHeader);
        cancelAutoUpdate();
        unregisterKeyListeners();
    });
</script>

<style lang="scss">
    @import url("@/assets/styles/pages/_details.scss");
</style>